import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';

import { FormControl, FormGroup } from '@angular/forms';

import { MatSnackBar } from '@angular/material/snack-bar';

import { Subscription } from 'rxjs';

import { filter } from 'rxjs/operators';

import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';

import {

  buildVersionAttributeFiltersPayload,

  DisplayColor,

  resolveFilterFieldDisplayName,

  StoreAttributeFilterCategoryViewModel,

  StoreAttributeFilterFieldViewModel,

  StoreAttributeValueType,

  StoreSourceSelectOption,

  VersionAttributeFilterSelection,

  VersionAttributeFilterPair,

  VersionAttributeSearchResult,

  VersionAttributeSearchSummary,

} from '../../models/store-attribute.model';

import { Version } from '../../models/version.model';

import { StoreAttributeService } from '../../services/store-attribute.service';

import { VersionService } from '../../services/version.service';



@Component({
    selector: 'app-version-attribute-search',
    templateUrl: './version-attribute-search.component.html',
    styleUrls: ['./version-attribute-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class VersionAttributeSearchComponent implements OnInit, OnDestroy {

  @Input() totalVersionCount = 0;

  @Input() compact = false;

  @Input() layout: 'browse' | 'association' | 'versions-list' = 'browse';

  @Input() applyMode: 'auto' | 'manual' = 'auto';

  @Input() eventId?: string;

  @Input() selectedVersionNames: string[] = [];

  @Input() includePartialMatches = false;

  @Output() filtersChange = new EventEmitter<VersionAttributeFilterSelection[]>();

  @Output() selectedVersionNamesChange = new EventEmitter<string[]>();

  @Output() includePartialMatchesChange = new EventEmitter<boolean>();



  readonly booleanType: StoreAttributeValueType = 'Boolean';

  readonly textType: StoreAttributeValueType = 'Text';

  readonly singleSelectType: StoreAttributeValueType = 'SingleSelect';

  readonly multiSelectType: StoreAttributeValueType = 'MultiSelect';



  categories: StoreAttributeFilterCategoryViewModel[] = [];

  categoriesLoading = true;

  categoriesLoadError = false;

  filterForm = new FormGroup({});

  activeSelections: VersionAttributeFilterSelection[] = [];

  appliedSelections: VersionAttributeFilterSelection[] = [];

  hasAppliedFilters = false;

  fieldByClientKey = new Map<string, StoreAttributeFilterFieldViewModel>();

  apiResults: VersionAttributeSearchResult[] = [];

  apiTotal = 0;

  attributeSearchLoading = false;

  expandedConflictVersionId: string | null = null;

  appliedFilterPairs: VersionAttributeFilterPair[] = [];



  private formSubscription?: Subscription;

  private categoriesSubscription?: Subscription;

  private searchSubscriptions: Subscription[] = [];



  constructor(

    private storeAttributeService: StoreAttributeService,

    private versionService: VersionService<Version>,

    private matSnackBar: MatSnackBar,

  ) {}



  ngOnInit(): void {

    this.categoriesSubscription = this.storeAttributeService

      .getFilterEnabledAttributesState()

      .subscribe((state) => {

        this.categoriesLoading = state.loading;

        this.categoriesLoadError = state.loadFailed;

        if (state.loading) {

          return;

        }

        this.categories = state.categories;

        this.buildFilterForm(state.categories);

      });



    this.searchSubscriptions.push(

      this.versionService.attributeSearchResults$.subscribe((results) => {

        this.apiResults = results;

      }),

      this.versionService.attributeSearchTotal$.subscribe((total) => {

        this.apiTotal = total;

      }),

      this.versionService.attributeSearchLoading$.subscribe((loading) => {

        this.attributeSearchLoading = loading;

      }),

      this.versionService.attributeSearchError$
        .pipe(filter((message): message is string => !!message))
        .subscribe((message) => {
          this.matSnackBar.open(message, 'OK', DEFAULT_SNACKBAR_CONFIG);
        }),

    );

  }



  ngOnDestroy(): void {

    this.formSubscription?.unsubscribe();

    this.categoriesSubscription?.unsubscribe();

    this.searchSubscriptions.forEach((sub) => sub.unsubscribe());

    this.versionService.setAttributeFilterPairs([]);

  }



  get isAssociationLayout(): boolean {

    return this.layout === 'association';

  }



  get isVersionsListLayout(): boolean {

    return this.layout === 'versions-list';

  }



  get isManualApply(): boolean {

    return this.applyMode === 'manual';

  }



  get hasActiveFilters(): boolean {

    return this.activeSelections.length > 0;

  }



  get searchSummary(): VersionAttributeSearchSummary {

    const matchingVersions = this.apiTotal || this.apiResults.length;

    const fullMatch = this.apiResults.filter((result) => result.matchType === 'full').length;

    const partialMatch = this.apiResults.filter((result) => result.matchType === 'partial').length;



    return {

      matchingVersions,

      fullMatch,

      partialMatch,

      totalStores: this.apiResults.reduce((sum, result) => sum + result.storeCount, 0),

    };

  }



  get filteredResultsTitle(): string {

    const selections = this.isVersionsListLayout ? this.appliedSelections : this.activeSelections;

    if (!selections.length) {

      return 'Filtered Versions';

    }



    return `Filtered Versions — ${selections.map((s) => s.displayValue).join(' / ')}`;

  }



  get fullMatchResults(): VersionAttributeSearchResult[] {

    return this.apiResults.filter((r) => r.matchType === 'full');

  }



  get partialMatchResults(): VersionAttributeSearchResult[] {

    return this.apiResults.filter((r) => r.matchType === 'partial');

  }



  get visibleAssociationResults(): VersionAttributeSearchResult[] {

    if (this.includePartialMatches) {

      return this.apiResults;

    }



    return this.fullMatchResults;

  }



  get allFullMatchesSelected(): boolean {

    const fullNames = this.fullMatchResults.map((r) => r.versionName);

    return fullNames.length > 0 && fullNames.every((name) => this.selectedVersionNames.includes(name));

  }



  get someFullMatchesSelected(): boolean {

    const fullNames = this.fullMatchResults.map((r) => r.versionName);

    const selectedCount = fullNames.filter((name) => this.selectedVersionNames.includes(name)).length;

    return selectedCount > 0 && selectedCount < fullNames.length;

  }



  getControl(clientKey: string): FormControl {

    return this.filterForm.get(clientKey) as FormControl;

  }



  getSelectOptions(field: StoreAttributeFilterFieldViewModel): StoreSourceSelectOption[] {

    if (field.selectOptions?.length) {

      return field.selectOptions;

    }



    return field.options.map((option) => ({

      value: option,

      label: option,

    }));

  }



  getFilterFieldDisplayName(field: StoreAttributeFilterFieldViewModel): string {
    return resolveFilterFieldDisplayName(field);
  }

  getOptionLabel(field: StoreAttributeFilterFieldViewModel, value: unknown): string {

    const stringValue = String(value);

    const match = this.getSelectOptions(field).find(

      (option) => option.value === stringValue,

    );



    return match?.label ?? stringValue;

  }



  isVersionSelected(versionName: string): boolean {

    return this.selectedVersionNames.includes(versionName);

  }



  isRowSelectable(result: VersionAttributeSearchResult): boolean {

    if (result.matchType === 'full') {

      return true;

    }



    return this.includePartialMatches;

  }



  toggleVersionSelection(result: VersionAttributeSearchResult): void {

    this.onVersionCheckboxChange(result, !this.isVersionSelected(result.versionName));

  }



  onVersionCheckboxChange(result: VersionAttributeSearchResult, checked: boolean): void {

    if (!this.isAssociationLayout) {

      return;

    }



    if (!this.isRowSelectable(result)) {

      return;

    }



    const next = checked

      ? [...this.selectedVersionNames, result.versionName]

      : this.selectedVersionNames.filter((name) => name !== result.versionName);



    this.emitSelectedVersionNames([...new Set(next)]);

  }



  onSelectAllFullMatchesChange(checked: boolean): void {

    const fullNames = this.fullMatchResults.map((r) => r.versionName);

    const partialNames = this.partialMatchResults.map((r) => r.versionName);



    if (checked) {

      const partialKept = this.includePartialMatches

        ? this.selectedVersionNames.filter((name) => partialNames.includes(name))

        : [];

      this.emitSelectedVersionNames([...fullNames, ...partialKept]);

      return;

    }



    const next = this.selectedVersionNames.filter((name) => !fullNames.includes(name));

    this.emitSelectedVersionNames(next);

  }



  onIncludePartialMatchesChange(checked: boolean): void {

    this.includePartialMatches = checked;

    this.includePartialMatchesChange.emit(checked);



    if (!checked) {

      const partialNames = new Set(this.partialMatchResults.map((r) => r.versionName));

      this.emitSelectedVersionNames(

        this.selectedVersionNames.filter((name) => !partialNames.has(name)),

      );

    }

  }



  removeFilter(clientKey: string): void {

    const field = this.fieldByClientKey.get(clientKey);

    if (!field) {

      return;

    }



    this.getControl(clientKey)?.setValue(this.defaultValueForField(field));

  }



  clearAll(): void {

    this.fieldByClientKey.forEach((field, clientKey) => {

      this.getControl(clientKey)?.setValue(this.defaultValueForField(field), { emitEvent: false });

    });

    this.activeSelections = [];

    this.appliedSelections = [];

    this.hasAppliedFilters = false;

    this.appliedFilterPairs = [];

    this.expandedConflictVersionId = null;

    this.versionService.setAttributeFilterPairs([]);

    this.versionService.setAttributeFilters([]);

    this.filtersChange.emit([]);

    if (this.isAssociationLayout) {

      this.emitSelectedVersionNames([]);

    }

  }



  applyFilters(): void {

    if (!this.hasActiveFilters) {

      return;

    }

    if (this.eventId) {

      this.versionService.eventId = this.eventId;

    }

    this.appliedSelections = [...this.activeSelections];

    this.hasAppliedFilters = true;

    const pairs = buildVersionAttributeFiltersPayload(

      this.filterForm.getRawValue(),

      this.fieldByClientKey.values(),

    );

    this.appliedFilterPairs = pairs;

    this.expandedConflictVersionId = null;

    this.versionService.setAttributeFilterPairs(pairs);

    this.versionService.setAttributeFilters(this.activeSelections);

    this.filtersChange.emit(this.activeSelections);

  }



  private emitSelectedVersionNames(names: string[]): void {

    this.selectedVersionNames = names;

    this.selectedVersionNamesChange.emit(names);

  }



  private buildFilterForm(categories: StoreAttributeFilterCategoryViewModel[]): void {

    this.formSubscription?.unsubscribe();

    this.fieldByClientKey.clear();



    const controls: Record<string, FormControl> = {};

    categories.forEach((category) => {

      category.fields.forEach((field) => {

        this.fieldByClientKey.set(field.clientKey, field);

        controls[field.clientKey] = new FormControl(this.defaultValueForField(field));

      });

    });



    this.filterForm = new FormGroup(controls);

    this.formSubscription = this.filterForm.valueChanges.subscribe((values) => {

      const selections = this.mapFormToSelections(values);

      this.activeSelections = selections;

      if (!this.isManualApply) {

        this.syncSelections(selections);

      }

    });

  }



  private defaultValueForField(field: StoreAttributeFilterFieldViewModel): unknown {

    if (field.valueType === 'Boolean') {

      return false;

    }



    if (field.valueType === 'MultiSelect') {

      return [];

    }



    return null;

  }



  private mapFormToSelections(

    values: Record<string, unknown>,

  ): VersionAttributeFilterSelection[] {

    const selections: VersionAttributeFilterSelection[] = [];



    this.fieldByClientKey.forEach((field, clientKey) => {

      const value = values[clientKey];



      if (field.valueType === 'Boolean') {

        if (!value) {

          return;

        }



        selections.push({

          clientKey,

          label: resolveFilterFieldDisplayName(field),

          displayValue: 'Yes',

          categoryColor: field.categoryColor,

        });

        return;

      }



      if (field.valueType === 'MultiSelect') {

        const selected = Array.isArray(value) ? value : [];

        if (!selected.length) {

          return;

        }



        selections.push({

          clientKey,

          label: resolveFilterFieldDisplayName(field),

          displayValue: selected

            .map((item) => this.getOptionLabel(field, item))

            .join(', '),

          categoryColor: field.categoryColor,

        });

        return;

      }



      if (field.valueType === 'Text') {

        const text = value == null ? '' : String(value).trim();

        if (!text) {

          return;

        }



        selections.push({

          clientKey,

          label: resolveFilterFieldDisplayName(field),

          displayValue: text,

          categoryColor: field.categoryColor,

        });

        return;

      }



      if (value === null || value === undefined || value === '') {

        return;

      }



      selections.push({

        clientKey,

        label: resolveFilterFieldDisplayName(field),

        displayValue: this.getOptionLabel(field, value),

        categoryColor: field.categoryColor,

      });

    });



    return selections;

  }



  private syncSelections(selections: VersionAttributeFilterSelection[]): void {

    this.activeSelections = selections;

    this.versionService.setAttributeFilters(selections);



    if (this.eventId) {

      this.versionService.eventId = this.eventId;

    }



    const pairs = buildVersionAttributeFiltersPayload(

      this.filterForm.getRawValue(),

      this.fieldByClientKey.values(),

    );

    this.versionService.setAttributeFilterPairs(pairs);

    this.filtersChange.emit(selections);



    if (this.isAssociationLayout && !selections.length) {

      this.emitSelectedVersionNames([]);

    }

  }



  chipBackground(color: DisplayColor): string {

    const map: Record<DisplayColor, string> = {

      blue: '#e3f2fd',

      red: '#ffebee',

      green: '#e8f5e9',

      orange: '#fff3e0',

      purple: '#f3e5f5',

      teal: '#e0f2f1',

      brown: '#efebe9',

      grey: '#f5f5f5',

    };

    return map[color] ?? '#f5f5f5';

  }



  chipBorder(color: DisplayColor): string {

    const map: Record<DisplayColor, string> = {

      blue: '#1976d2',

      red: '#d32f2f',

      green: '#388e3c',

      orange: '#f57c00',

      purple: '#7b1fa2',

      teal: '#00897b',

      brown: '#6d4c41',

      grey: '#757575',

    };

    return map[color] ?? '#757575';

  }



  categoryUsesOnlyBooleans(category: StoreAttributeFilterCategoryViewModel): boolean {

    return (

      category.fields.length > 0 &&

      category.fields.every((field) => field.valueType === this.booleanType)

    );

  }



  categoryUsesOnlySelects(category: StoreAttributeFilterCategoryViewModel): boolean {

    return (

      category.fields.length > 0 &&

      category.fields.every((field) => field.valueType === this.singleSelectType)

    );

  }



  categoryUsesOnlyMultiSelects(category: StoreAttributeFilterCategoryViewModel): boolean {

    return (

      category.fields.length > 0 &&

      category.fields.every((field) => field.valueType === this.multiSelectType)

    );

  }



  getCategoryDotClass(color: DisplayColor): string {

    const map: Record<DisplayColor, string> = {

      blue: 'dot-geo',

      red: 'dot-alc',

      green: 'dot-health',

      orange: 'dot-fresh',

      purple: 'dot-svc',

      teal: 'dot-svc',

      brown: 'dot-svc',

      grey: 'dot-svc',

    };

    return map[color] ?? 'dot-geo';

  }



  getCategoryTagClass(color: DisplayColor): string {

    const map: Record<DisplayColor, string> = {

      blue: 'ftag-geo',

      red: 'ftag-alc',

      green: 'ftag-health',

      orange: 'ftag-fresh',

      purple: 'ftag-svc',

      teal: 'ftag-svc',

      brown: 'ftag-svc',

      grey: 'ftag-geo',

    };

    return map[color] ?? 'ftag-geo';

  }



  getTagClass(colorHex: string): string {

    if (colorHex.includes('1976d2') || colorHex.includes('2196f3')) {

      return 'tag-geo';

    }

    if (colorHex.includes('d32f2f') || colorHex.includes('f44336')) {

      return 'tag-alc';

    }

    if (colorHex.includes('388e3c') || colorHex.includes('4caf50')) {

      return 'tag-health';

    }

    if (colorHex.includes('f57c00') || colorHex.includes('ff9800')) {

      return 'tag-fresh';

    }

    if (colorHex.includes('7b1fa2') || colorHex.includes('9c27b0')) {

      return 'tag-svc';

    }

    return 'tag-geo';

  }



  formatTagLabel(tag: { label: string; value: string; coveragePercent?: number }): string {

    if (tag.coveragePercent != null && tag.coveragePercent < 100) {

      return `${tag.label} (${tag.coveragePercent}%)`;

    }



    return tag.label;

  }

  getMatchTooltip(result: VersionAttributeSearchResult): string {
    if (result.matchType === 'full') {
      return `Full match: All ${result.storeCount} stores match all active filter criteria`;
    }

    return `Partial match: ${result.matchPercent}% of stores match all active filter criteria`;
  }

  toggleConflictDetail(result: VersionAttributeSearchResult, event?: Event): void {
    event?.stopPropagation();
    if (!result.versionId || !this.hasAppliedFilters) {
      return;
    }

    this.expandedConflictVersionId =
      this.expandedConflictVersionId === result.versionId ? null : result.versionId;
  }

  isConflictDetailExpanded(result: VersionAttributeSearchResult): boolean {
    return !!result.versionId && this.expandedConflictVersionId === result.versionId;
  }

}


